import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-0.5" };
const _hoisted_2 = ["id"];
const _hoisted_3 = ["id"];
const _hoisted_4 = { class: "flex flex-col gap-6" };
const _hoisted_5 = { class: "w-full sm:max-w-[358px]" };
import { computed, ref, watch, useId } from "vue";
import { useI18n } from "vue-i18n";
import { AutoComplete, Button, Modal, SelectInput } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { useContactSearch } from "@/modules/contact/public";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import { useAppToast } from "@/shared/composables/useAppToast";
import { isoToDisplayDate, displayDateToIso } from "@/shared/utils/date-formatting";
import { createOrganisationRelation } from "../../../infrastructure/api/organisationRelationsApi";
import { getContactById } from "@/modules/contact/infrastructure/api/contactApi";
import { logger } from "@/infrastructure/services/LoggerService";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import CreateContactModal from "@/modules/contact/presentation/components/CreateContactModal.vue";
import RelationFunctionRows from "@/modules/contact/presentation/components/RelationFunctionRows.vue";
// ─── Props / emits ─────────────────────────────────────────────────────────────
export default /*@__PURE__*/ _defineComponent({
    __name: 'CreateOrganisationRelationModal',
    props: /*@__PURE__*/ _mergeModels({
        organisationId: {},
        organisationName: {}
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const open = _useModel(__props, "modelValue");
        const props = __props;
        const emit = __emit;
        // ─── Dependencies ──────────────────────────────────────────────────────────────
        const { locale } = useI18n();
        const { canEditOphub: canEdit } = usePermissions();
        const profileStore = useProfileStore();
        const { toastSuccess, toastError } = useAppToast();
        const titleId = useId();
        const descriptionId = useId();
        // ─── Contact search ────────────────────────────────────────────────────────────
        const { suggestions: contactSuggestions, isLoading: contactLoading, isError: contactError, search: contactSearch, clear: contactClear } = useContactSearch();
        const selectedContactId = ref("");
        // ─── Function rows ─────────────────────────────────────────────────────────────
        function emptyRow() {
            const todayIso = new Date().toISOString().slice(0, 10);
            return {
                _id: crypto.randomUUID(),
                title: "",
                function: "",
                start: isoToDisplayDate(todayIso, profileStore.profile?.dateFormat, locale.value),
                end: "",
            };
        }
        const functionRows = ref([emptyRow()]);
        const functionRowsRef = ref();
        // ─── Save gating ───────────────────────────────────────────────────────────────
        const canSave = computed(() => canEdit.value &&
            !!selectedContactId.value &&
            functionRows.value.length > 0 &&
            functionRows.value.every((r) => !!r.function) &&
            !(functionRowsRef.value?.hasErrors ?? false));
        // ─── Nested: Create new contact on the fly ─────────────────────────────────────
        const showCreateContact = ref(false);
        const createContactMounted = ref(false);
        watch(showCreateContact, (v) => {
            if (v)
                createContactMounted.value = true;
        });
        function openCreateContact() {
            showCreateContact.value = true;
        }
        async function onContactCreated(contactId, relationTitle) {
            showCreateContact.value = false;
            // Fetch name first so the AutoComplete never briefly shows the raw ID as label.
            try {
                const contact = await getContactById(contactId);
                const name = `${contact.firstName} ${contact.lastName}`.trim() || contactId;
                contactSuggestions.value = [{ value: contactId, label: name }];
            }
            catch (err) {
                logger.debug("CreateOrganisationRelationModal: contact name lookup failed, falling back to id", err);
                contactSuggestions.value = [{ value: contactId, label: contactId }];
            }
            selectedContactId.value = contactId;
            if (relationTitle) {
                const firstRow = functionRows.value[0];
                if (!firstRow.title.trim()) {
                    functionRows.value[0] = { ...firstRow, title: relationTitle };
                }
                else {
                    const todayIso = new Date().toISOString().slice(0, 10);
                    functionRows.value.push({
                        _id: crypto.randomUUID(),
                        title: relationTitle,
                        function: "",
                        start: isoToDisplayDate(todayIso, profileStore.profile?.dateFormat, locale.value),
                        end: "",
                    });
                }
            }
        }
        // ─── Submit ────────────────────────────────────────────────────────────────────
        const saving = ref(false);
        const RELATION_ERROR_KEYS = {
            "Relation.Function.InvalidDateRange": "contact.detail.relations.errors.invalidDateRange",
        };
        async function handleSubmit() {
            if (!canSave.value)
                return;
            saving.value = true;
            try {
                await createOrganisationRelation(props.organisationId, {
                    contactId: selectedContactId.value,
                    functions: functionRows.value.map((row) => ({
                        title: row.title.trim() || null,
                        function: row.function,
                        startDate: displayDateToIso(row.start, profileStore.profile?.dateFormat, locale.value) ||
                            new Date().toISOString().slice(0, 10),
                        endDate: row.end
                            ? displayDateToIso(row.end, profileStore.profile?.dateFormat, locale.value) || null
                            : null,
                    })),
                });
                toastSuccess("contact.detail.relations.create.success");
                emit("created");
                open.value = false;
                resetForm();
            }
            catch (err) {
                const code = getApiErrorCode(err);
                const mappedKey = code ? RELATION_ERROR_KEYS[code] : undefined;
                if (!mappedKey) {
                    logger.error("CreateOrganisationRelationModal: save failed", err);
                }
                toastError(mappedKey ?? "contact.detail.relations.create.error");
            }
            finally {
                saving.value = false;
            }
        }
        // ─── Cancel / reset ────────────────────────────────────────────────────────────
        function resetForm() {
            selectedContactId.value = "";
            functionRows.value = [emptyRow()];
            contactClear();
        }
        function handleCancel() {
            open.value = false;
            resetForm();
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(Modal), {
                    modelValue: open.value,
                    "onUpdate:modelValue": [
                        _cache[3] || (_cache[3] = ($event) => ((open).value = $event)),
                        _cache[4] || (_cache[4] = (v) => !v && handleCancel())
                    ],
                    size: "xl",
                    "header-position": "center",
                    "footer-position": "split",
                    "show-close-button": true,
                    "show-header-border": false,
                    "show-footer-border": false,
                    "content-class": "min-h-[822px]",
                    "aria-labelledby": _unref(titleId),
                    "aria-describedby": _unref(descriptionId)
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                            _createElementVNode("h2", {
                                id: _unref(titleId),
                                class: "text-[length:var(--size-xxl)] font-[var(--weight-bold)]"
                            }, _toDisplayString(_ctx.$t("contact.detail.relations.create.title")), 9 /* TEXT, PROPS */, _hoisted_2),
                            _createElementVNode("p", {
                                id: _unref(descriptionId),
                                class: "text-[length:var(--size-l)] text-(--text-text-body-subtle)"
                            }, _toDisplayString(_ctx.$t("organisation.detail.contacts.relationCreate.subtitle")), 9 /* TEXT, PROPS */, _hoisted_3)
                        ])
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _ctx.$t('common.cancel'),
                            onClick: handleCancel
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            label: _ctx.$t('common.save'),
                            loading: saving.value,
                            disabled: !canSave.value,
                            onClick: handleSubmit
                        }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                            _createElementVNode("div", _hoisted_5, [
                                _createVNode(_unref(SelectInput), {
                                    "model-value": props.organisationId,
                                    label: _ctx.$t('organisation.detail.contacts.relationCreate.fields.organisation.label'),
                                    options: [{ value: props.organisationId, label: props.organisationName }],
                                    required: true,
                                    disabled: true,
                                    "full-width": ""
                                }, null, 8 /* PROPS */, ["model-value", "label", "options"])
                            ]),
                            _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_ctx.$t), {
                                modelValue: selectedContactId.value,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((selectedContactId).value = $event)),
                                label: _ctx.$t('organisation.detail.contacts.relationCreate.fields.contact.label'),
                                placeholder: _ctx.$t('organisation.detail.contacts.relationCreate.fields.contact.placeholder'),
                                "empty-label": _ctx.$t('organisation.detail.contacts.relationCreate.fields.contact.empty'),
                                options: _unref(contactSuggestions),
                                loading: _unref(contactLoading),
                                error: _unref(contactError) ? _ctx.$t('organisation.detail.contacts.relationCreate.fields.contact.searchError') : undefined,
                                required: true,
                                disabled: !_unref(canEdit),
                                "icon-left": "ri-user-line",
                                "icon-right": "ri-search-line",
                                "full-width": "",
                                "create-label": _ctx.$t('common.createNewContact'),
                                onSearch: _unref(contactSearch),
                                onClear: _unref(contactClear),
                                onCreate: _cache[1] || (_cache[1] = ($event) => (openCreateContact()))
                            }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "empty-label", "options", "loading", "error", "disabled", "create-label", "onSearch", "onClear"]),
                            _createVNode(RelationFunctionRows, {
                                ref_key: "functionRowsRef",
                                ref: functionRowsRef,
                                modelValue: functionRows.value,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((functionRows).value = $event)),
                                "require-title": true
                            }, null, 8 /* PROPS */, ["modelValue"])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "aria-labelledby", "aria-describedby"]),
                (createContactMounted.value)
                    ? (_openBlock(), _createBlock(CreateContactModal, {
                        key: 0,
                        modelValue: showCreateContact.value,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((showCreateContact).value = $event)),
                        "redirect-on-create": false,
                        "initial-organisation-id": props.organisationId,
                        "initial-organisation-label": props.organisationName,
                        "create-default-relation": false,
                        onCreated: onContactCreated
                    }, null, 8 /* PROPS */, ["modelValue", "initial-organisation-id", "initial-organisation-label"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
